import React, { useState, useCallback } from "react";
import {
  Card,
  Typography,
  Button,
  Space,
  Table,
  message,
  Row,
  Col,
  Input,
  Tabs,
  Tag,
  Statistic,
  Modal,
  Spin,
  Divider,
} from "antd";
import {
  SearchOutlined,
  FileExcelOutlined,
  ReloadOutlined,
  CheckCircleFilled,
  FileTextFilled,
  DatabaseFilled,
  RobotOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import axiosInstance from "../../configs";
import { getUser } from "../../utils/DataUser";

const { Title, Text } = Typography;

/* ───────────────────────────────────────────────
   Helper: user role params
─────────────────────────────────────────────── */
function getUserParams() {
  const user = getUser();
  const userLevel = String(user?.id_user_level || user?.id_level || "");
  const extra = {};
  if (userLevel === "7") extra.id_customer = user.username;
  return extra;
}

/* ───────────────────────────────────────────────
   Shared base columns (tab Dokumen + Fisik)
─────────────────────────────────────────────── */
function buildBaseColumns(pagination) {
  return [
    {
      title: "No",
      key: "no",
      width: 50,
      align: "center",
      fixed: "left",
      render: (_, __, idx) =>
        idx + (pagination.current - 1) * pagination.pageSize + 1,
    },
    {
      title: "Importir",
      dataIndex: "importir",
      width: 180,
      ellipsis: true,
      render: (v) => v || "-",
    },
    {
      title: "Nopen",
      dataIndex: "nopen",
      width: 120,
      ellipsis: true,
      render: (v) => v || "-",
    },
    {
      title: "Kode Barang",
      dataIndex: "item_code",
      width: 120,
      ellipsis: true,
      render: (v) => v || "-",
    },
    {
      title: "Nama Barang",
      dataIndex: "item_name",
      width: 220,
      ellipsis: true,
      render: (v) => v || "-",
    },
    {
      title: "Ukuran",
      dataIndex: "ukuran",
      width: 140,
      ellipsis: true,
      render: (v) => v || "-",
    },
    {
      title: <span style={{ color: "#cf1322" }}>PCS</span>,
      dataIndex: "pcs",
      width: 75,
      align: "right",
      render: (v) => (
        <span style={{ color: "#cf1322", fontWeight: 700 }}>
          {v != null ? Number(v).toLocaleString("id-ID") : "-"}
        </span>
      ),
    },
    {
      title: "Carton",
      dataIndex: "carton",
      width: 90,
      align: "right",
      render: (v) => (
        <span style={{ fontWeight: 600 }}>
          {v != null ? Number(v).toLocaleString("id-ID") : "-"}
        </span>
      ),
    },
    {
      title: "Botol/PCE",
      dataIndex: "botol",
      width: 100,
      align: "right",
      render: (v) => (
        <span style={{ fontWeight: 600 }}>
          {v != null ? Number(v).toLocaleString("id-ID") : "-"}
        </span>
      ),
    },
  ];
}

/* ───────────────────────────────────────────────
   Shared toolbar
─────────────────────────────────────────────── */
function Toolbar({
  searchText,
  setSearchText,
  onSearch,
  onReset,
  onExport,
  exportLoading,
}) {
  return (
    <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
      <Col flex="auto">
        <Space wrap>
          <Input
            placeholder="Cari kode/nama/nopen..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={onSearch}
            allowClear
            style={{ width: 220 }}
            prefix={<SearchOutlined />}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={onSearch}>
            Cari
          </Button>
          <Button icon={<ReloadOutlined />} onClick={onReset}>
            Reset
          </Button>
          {onExport && (
            <Button
              icon={<FileExcelOutlined />}
              onClick={onExport}
              loading={exportLoading}
              disabled={exportLoading}
            >
              {exportLoading ? "Membuat file..." : "Excel"}
            </Button>
          )}
        </Space>
      </Col>
    </Row>
  );
}

/* ───────────────────────────────────────────────
   TAB 1 – Stok Berdasarkan Dokumen
─────────────────────────────────────────────── */
function StokDokumen() {
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchData = useCallback(
    async (page = 1, pageSize = 10, search = "") => {
      setLoading(true);
      try {
        const params = { page, limit: pageSize, ...getUserParams() };
        if (search.trim()) params.search = search.trim();
        const res = await axiosInstance.get("/report/stok-dokumen", { params });
        setData(res.data.data || []);
        setPagination({
          current: res.data.page || page,
          pageSize: res.data.limit || pageSize,
          total: res.data.total || 0,
        });
      } catch (e) {
        console.error(e);
        message.error("Gagal load stok dokumen");
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = () => fetchData(1, pagination.pageSize, searchText);
  const handleReset = () => {
    setSearchText("");
    fetchData(1, 10, "");
  };
  const handleTable = (p) => fetchData(p.current, p.pageSize, searchText);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const params = {
        ...getUserParams(),
        ...(searchText.trim() ? { search: searchText.trim() } : {}),
      };
      const res = await axiosInstance.get("/report/stok-dokumen/export", {
        params,
        responseType: "blob",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(
        new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
      );
      link.download = "stok_dokumen.xlsx";
      link.click();
      message.success("Export berhasil");
    } catch {
      message.error("Gagal export Excel");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <>
      <Toolbar
        searchText={searchText}
        setSearchText={setSearchText}
        onSearch={handleSearch}
        onReset={handleReset}
        onExport={handleExport}
        exportLoading={exportLoading}
      />
      <Table
        size="small"
        bordered
        loading={loading}
        columns={buildBaseColumns(pagination)}
        dataSource={data}
        rowKey={(r, i) => `dok-${r.nopen}-${r.item_code}-${i}`}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          size: "small",
          showTotal: (t, r) => `${r[0]}-${r[1]} dari ${t}`,
          pageSizeOptions: ["10", "20", "50", "100"],
        }}
        onChange={handleTable}
        scroll={{ x: 1200 }}
      />
    </>
  );
}

/* ───────────────────────────────────────────────
   TAB 2 – Stok Berdasarkan Fisik
─────────────────────────────────────────────── */
function StokFisik() {
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchData = useCallback(
    async (page = 1, pageSize = 10, search = "") => {
      setLoading(true);
      try {
        const params = { page, limit: pageSize, ...getUserParams() };
        if (search.trim()) params.search = search.trim();
        const res = await axiosInstance.get("/report/stok-fisik", { params });
        setData(res.data.data || []);
        setPagination({
          current: res.data.page || page,
          pageSize: res.data.limit || pageSize,
          total: res.data.total || 0,
        });
      } catch (e) {
        console.error(e);
        message.error("Gagal load stok fisik");
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = () => fetchData(1, pagination.pageSize, searchText);
  const handleReset = () => {
    setSearchText("");
    fetchData(1, 10, "");
  };
  const handleTable = (p) => fetchData(p.current, p.pageSize, searchText);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const params = {
        ...getUserParams(),
        ...(searchText.trim() ? { search: searchText.trim() } : {}),
      };
      const res = await axiosInstance.get("/report/stok-fisik/export", {
        params,
        responseType: "blob",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(
        new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
      );
      link.download = "stok_fisik.xlsx";
      link.click();
      message.success("Export berhasil");
    } catch {
      message.error("Gagal export Excel");
    } finally {
      setExportLoading(false);
    }
  };

  const columns = [
    ...buildBaseColumns(pagination),
    {
      title: "Lot",
      dataIndex: "lot",
      width: 90,
      render: (v) => (v ? <Tag color="blue">{v}</Tag> : "-"),
    },
  ];

  return (
    <>
      <Toolbar
        searchText={searchText}
        setSearchText={setSearchText}
        onSearch={handleSearch}
        onReset={handleReset}
        onExport={handleExport}
        exportLoading={exportLoading}
      />
      <Table
        size="small"
        bordered
        loading={loading}
        columns={columns}
        dataSource={data}
        rowKey={(r, i) => `fisik-${r.nopen}-${r.item_code}-${r.lot}-${i}`}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          size: "small",
          showTotal: (t, r) => `${r[0]}-${r[1]} dari ${t}`,
          pageSizeOptions: ["10", "20", "50", "100"],
        }}
        onChange={handleTable}
        scroll={{ x: 1300 }}
      />
    </>
  );
}

/* ───────────────────────────────────────────────
   TAB 3 – Komparasi Dokumen vs Fisik
─────────────────────────────────────────────── */
const STATUS_CONFIG = {
  match: {
    color: "#52c41a",
    bg: "#f6ffed",
    border: "#b7eb8f",
    label: "Sesuai",
    tagColor: "success",
  },
  lebih_dok: {
    color: "#faad14",
    bg: "#fffbe6",
    border: "#ffe58f",
    label: "Dok > Fisik",
    tagColor: "warning",
  },
  lebih_fisik: {
    color: "#ff4d4f",
    bg: "#fff2f0",
    border: "#ffccc7",
    label: "Fisik > Dok",
    tagColor: "error",
  },
};

function getStatus(selisih) {
  const s = Number(selisih);
  if (s === 0) return "match";
  if (s > 0) return "lebih_dok";
  return "lebih_fisik";
}

/* ── Modal: daftar item dalam satu kategori ── */
const MODAL_COLS = [
  {
    title: "No",
    key: "no",
    width: 45,
    align: "center",
    render: (_, __, i) => i + 1,
  },
  {
    title: "Importir",
    dataIndex: "importir",
    width: 150,
    ellipsis: true,
    render: (v) => v || "-",
  },
  { title: "Nopen", dataIndex: "nopen", width: 115, ellipsis: true },
  { title: "Kode", dataIndex: "item_code", width: 110, ellipsis: true },
  { title: "Nama Barang", dataIndex: "item_name", width: 200, ellipsis: true },
  {
    title: "Carton Dok",
    dataIndex: "carton_dok",
    width: 95,
    align: "right",
    render: (v) => (
      <span style={{ fontWeight: 600 }}>
        {Number(v || 0).toLocaleString("id-ID")}
      </span>
    ),
  },
  {
    title: "Carton Fisik",
    dataIndex: "carton_fisik",
    width: 98,
    align: "right",
    render: (v) => (
      <span style={{ fontWeight: 600 }}>
        {Number(v || 0).toLocaleString("id-ID")}
      </span>
    ),
  },
  {
    title: "Selisih",
    dataIndex: "selisih",
    width: 80,
    align: "right",
    render: (v) => {
      const n = Number(v);
      const color = n === 0 ? "#52c41a" : n > 0 ? "#faad14" : "#ff4d4f";
      return (
        <span style={{ fontWeight: 700, color }}>
          {n > 0 ? "+" : ""}
          {n.toLocaleString("id-ID")}
        </span>
      );
    },
  },
];

function ItemModal({ open, onClose, statusKey, items }) {
  if (!statusKey) return null;
  const cfg = STATUS_CONFIG[statusKey];
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Tutup</Button>}
      title={
        <span>
          <Tag color={cfg.tagColor} style={{ marginRight: 8 }}>
            {cfg.label}
          </Tag>
          {items.length.toLocaleString("id-ID")} item
        </span>
      }
      width={900}
      styles={{ body: { maxHeight: "65vh", overflowY: "auto" } }}
    >
      <Table
        size="small"
        bordered
        dataSource={items}
        columns={MODAL_COLS}
        rowKey={(r, i) => `modal-${r.nopen}-${r.item_code}-${i}`}
        pagination={{
          pageSize: 20,
          size: "small",
          showTotal: (t, r) => `${r[0]}-${r[1]} dari ${t}`,
        }}
        scroll={{ x: 900 }}
        rowClassName={(r) => {
          const st = getStatus(r.selisih);
          return st === "match"
            ? "row-match"
            : st === "lebih_dok"
              ? "row-lebih-dok"
              : "row-lebih-fisik";
        }}
      />
    </Modal>
  );
}

/* simple markdown → safe HTML */
function renderMarkdown(md) {
  if (!md) return "";
  return md
    .replace(
      /^## (.+)$/gm,
      "<h3 style='margin:14px 0 6px;color:#1a237e;font-size:14px;border-left:3px solid #1a237e;padding-left:8px'>$1</h3>",
    )
    .replace(
      /^### (.+)$/gm,
      "<h4 style='margin:10px 0 4px;color:#333;font-size:13px'>$1</h4>",
    )
    .replace(/\*\*(.+?)\*\*/g, "<b>$1</b>")
    .replace(
      /^[-*] (.+)$/gm,
      "<li style='margin-left:16px;margin-bottom:4px'>$1</li>",
    )
    .replace(/\n\n/g, "<br/><br/>")
    .replace(/\n/g, "<br/>");
}

function StokKomparasi() {
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [allData, setAllData] = useState([]); // semua row untuk AI
  const [summary, setSummary] = useState({});
  const [filterStatus, setFilterStatus] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalStatus, setModalStatus] = useState(null);

  // AI state
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiError, setAiError] = useState(null);

  const fetchData = useCallback(
    async (page = 1, pageSize = 20, search = "") => {
      setLoading(true);
      try {
        const params = { page, limit: pageSize, ...getUserParams() };
        if (search.trim()) params.search = search.trim();
        const res = await axiosInstance.get("/report/stok-komparasi", {
          params,
        });
        setData(res.data.data || []);
        setSummary(res.data.summary || {});
        setPagination({
          current: res.data.page || page,
          pageSize: res.data.limit || pageSize,
          total: res.data.total || 0,
        });
        // load semua data (untuk AI + modal) hanya di halaman 1
        if (page === 1) {
          const resAll = await axiosInstance.get("/report/stok-komparasi", {
            params: { ...params, limit: 9999, page: 1 },
          });
          setAllData(resAll.data.data || []);
        }
      } catch (e) {
        console.error(e);
        message.error("Gagal load komparasi");
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = () => {
    setFilterStatus(null);
    setAiResult(null);
    fetchData(1, pagination.pageSize, searchText);
  };
  const handleReset = () => {
    setSearchText("");
    setFilterStatus(null);
    setAiResult(null);
    fetchData(1, 20, "");
  };
  const handleTable = (p) => fetchData(p.current, p.pageSize, searchText);

  /* Klik kartu → buka modal dengan item-item di kategori tsb */
  const openModal = (key) => {
    setModalStatus(key);
    setModalOpen(true);
  };

  /* Filter baris tabel saat kartu diklik */
  const handleCardClick = (key) => {
    if (filterStatus === key) {
      setFilterStatus(null); // toggle off
    } else {
      setFilterStatus(key);
    }
  };

  /* AI analysis — hanya kirim baris anomali (selisih ≠ 0), max 200 baris */
  const handleAiAnalysis = async () => {
    setAiLoading(true);
    setAiResult(null);
    setAiError(null);
    try {
      // Filter hanya anomali agar payload tetap kecil
      const anomaliRows = allData
        .filter((r) => Number(r.selisih) !== 0)
        .sort(
          (a, b) => Math.abs(Number(b.selisih)) - Math.abs(Number(a.selisih)),
        )
        .slice(0, 200);

      const res = await axiosInstance.post("/report/stok-komparasi/analisis", {
        data: anomaliRows,
        summary,
      });
      setAiResult(res.data);
    } catch (e) {
      const errMsg =
        e?.response?.data?.error || "Gagal menghubungi Claude Opus";
      setAiError(errMsg);
      message.error(errMsg);
    } finally {
      setAiLoading(false);
    }
  };

  /* Items per status (dari allData, bukan page aktif) */
  const itemsByStatus = (key) =>
    allData.filter((r) => getStatus(r.selisih) === key);

  const displayData = filterStatus
    ? data.filter((r) => getStatus(r.selisih) === filterStatus)
    : data;

  const CARDS = [
    {
      key: "match",
      icon: <CheckCircleFilled />,
      label: "Sesuai (Match)",
      value: summary.match ?? 0,
      color: "#52c41a",
      bg: "#f6ffed",
      border: "#b7eb8f",
      tagColor: "success",
    },
    {
      key: "lebih_dok",
      icon: <FileTextFilled />,
      label: "Dokumen > Fisik",
      value: summary.lebih_dok ?? 0,
      color: "#faad14",
      bg: "#fffbe6",
      border: "#ffe58f",
      tagColor: "warning",
    },
    {
      key: "lebih_fisik",
      icon: <DatabaseFilled />,
      label: "Fisik > Dokumen",
      value: summary.lebih_fisik ?? 0,
      color: "#ff4d4f",
      bg: "#fff2f0",
      border: "#ffccc7",
      tagColor: "error",
    },
  ];

  const columns = [
    {
      title: "No",
      key: "no",
      width: 50,
      align: "center",
      fixed: "left",
      render: (_, __, i) =>
        i + (pagination.current - 1) * pagination.pageSize + 1,
    },
    {
      title: "Importir",
      dataIndex: "importir",
      width: 160,
      ellipsis: true,
      render: (v) => v || "-",
    },
    {
      title: "Nopen",
      dataIndex: "nopen",
      width: 115,
      ellipsis: true,
      render: (v) => v || "-",
    },
    {
      title: "Kode Barang",
      dataIndex: "item_code",
      width: 115,
      ellipsis: true,
      render: (v) => v || "-",
    },
    {
      title: "Nama Barang",
      dataIndex: "item_name",
      width: 210,
      ellipsis: true,
      render: (v) => v || "-",
    },
    {
      title: "Ukuran",
      dataIndex: "ukuran",
      width: 130,
      ellipsis: true,
      render: (v) => v || "-",
    },
    {
      title: <span style={{ color: "#cf1322" }}>PCS</span>,
      dataIndex: "pcs",
      width: 70,
      align: "right",
      render: (v) => (
        <span style={{ color: "#cf1322", fontWeight: 700 }}>
          {Number(v || 0).toLocaleString("id-ID")}
        </span>
      ),
    },
    {
      title: "Carton Dok",
      dataIndex: "carton_dok",
      width: 100,
      align: "right",
      render: (v) => (
        <span style={{ fontWeight: 600 }}>
          {Number(v || 0).toLocaleString("id-ID")}
        </span>
      ),
    },
    {
      title: "Carton Fisik",
      dataIndex: "carton_fisik",
      width: 105,
      align: "right",
      render: (v) => (
        <span style={{ fontWeight: 600 }}>
          {Number(v || 0).toLocaleString("id-ID")}
        </span>
      ),
    },
    {
      title: "Selisih",
      dataIndex: "selisih",
      width: 90,
      align: "right",
      sorter: (a, b) => Number(a.selisih) - Number(b.selisih),
      render: (v) => {
        const n = Number(v);
        const color = n === 0 ? "#52c41a" : n > 0 ? "#faad14" : "#ff4d4f";
        return (
          <span style={{ fontWeight: 700, color }}>
            {n > 0 ? "+" : ""}
            {n.toLocaleString("id-ID")}
          </span>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 110,
      align: "center",
      sorter: (a, b) =>
        getStatus(a.selisih).localeCompare(getStatus(b.selisih)),
      render: (_, r) => {
        const st = getStatus(r.selisih);
        const cfg = STATUS_CONFIG[st];
        return <Tag color={cfg.tagColor}>{cfg.label}</Tag>;
      },
    },
  ];

  return (
    <>
      {/* ── Summary Cards ── */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {CARDS.map((c) => (
          <Col key={c.key} xs={24} sm={8}>
            <Card
              size="small"
              hoverable
              style={{
                backgroundColor: c.bg,
                border: `2px solid ${filterStatus === c.key ? c.color : c.border}`,
                borderRadius: 8,
                cursor: "pointer",
                transition: "box-shadow .2s",
                boxShadow:
                  filterStatus === c.key ? `0 0 0 3px ${c.color}33` : undefined,
              }}
              bodyStyle={{ padding: "10px 14px" }}
            >
              <Row align="middle" gutter={10}>
                <Col>
                  <span style={{ fontSize: 26, color: c.color }}>{c.icon}</span>
                </Col>
                <Col flex="auto" onClick={() => handleCardClick(c.key)}>
                  <div style={{ fontSize: 11, color: "#888" }}>{c.label}</div>
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      color: c.color,
                      lineHeight: 1.3,
                    }}
                  >
                    {c.value.toLocaleString("id-ID")}
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 400,
                        color: "#aaa",
                        marginLeft: 4,
                      }}
                    >
                      item
                    </span>
                  </div>
                </Col>
                {/* Tombol lihat detail */}
                <Col>
                  <Button
                    size="small"
                    type="link"
                    style={{ color: c.color, fontSize: 11, padding: "0 4px" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      openModal(c.key);
                    }}
                  >
                    Lihat →
                  </Button>
                </Col>
              </Row>
            </Card>
          </Col>
        ))}

        {/* Info total + filter tag */}
        <Col xs={24}>
          <Space size={4} wrap>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Total <b>{(summary.total_items ?? 0).toLocaleString("id-ID")}</b>{" "}
              kombinasi nopen × item. Klik kartu untuk filter tabel, atau{" "}
              <b>Lihat →</b> untuk detail per kategori.
            </Text>
            {filterStatus && (
              <Tag
                closable
                onClose={() => setFilterStatus(null)}
                color={STATUS_CONFIG[filterStatus].tagColor}
                style={{ marginLeft: 6 }}
              >
                Filter: {STATUS_CONFIG[filterStatus].label}
              </Tag>
            )}
          </Space>
        </Col>
      </Row>

      {/* ── Toolbar + tombol AI ── */}
      <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
        <Col flex="auto">
          <Space wrap>
            <Input
              placeholder="Cari kode/nama/nopen..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              allowClear
              style={{ width: 220 }}
              prefix={<SearchOutlined />}
            />
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
            >
              Cari
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              Reset
            </Button>
            <Divider type="vertical" />
            <Button
              icon={<RobotOutlined />}
              type="dashed"
              loading={aiLoading}
              disabled={aiLoading || allData.length === 0}
              onClick={handleAiAnalysis}
              style={{ borderColor: "#722ed1", color: "#722ed1" }}
            >
              {aiLoading ? "Menganalisis..." : "Analisis AI (Opus)"}
            </Button>
            {aiResult && (
              <Button
                size="small"
                icon={<CloseCircleOutlined />}
                onClick={() => setAiResult(null)}
                danger
              >
                Hapus Analisis
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      {/* ── Panel hasil AI ── */}
      {aiLoading && (
        <Card
          style={{
            marginBottom: 16,
            background: "#f9f0ff",
            border: "1px solid #d3adf7",
          }}
        >
          <Spin tip="Claude Opus sedang menganalisis anomali..." />
        </Card>
      )}
      {aiError && !aiLoading && (
        <Card
          style={{
            marginBottom: 16,
            background: "#fff1f0",
            border: "1px solid #ffccc7",
          }}
        >
          <Text type="danger">⚠️ {aiError}</Text>
        </Card>
      )}
      {aiResult && !aiLoading && (
        <Card
          size="small"
          style={{
            marginBottom: 16,
            background: "#f9f0ff",
            border: "1px solid #b37feb",
          }}
          title={
            <Space>
              <RobotOutlined style={{ color: "#722ed1" }} />
              <Text strong style={{ color: "#722ed1" }}>
                Analisis Anomali — Claude Opus
              </Text>
              <Tag color="purple">{aiResult.model}</Tag>
              <Tag color="orange">
                {aiResult.total_anomali} anomali dianalisis
              </Tag>
            </Space>
          }
        >
          <div
            style={{ fontSize: 13, lineHeight: 1.8, color: "#333" }}
            dangerouslySetInnerHTML={{
              __html: renderMarkdown(aiResult.analisis),
            }}
          />
        </Card>
      )}

      {/* ── Tabel Komparasi ── */}
      <Table
        size="small"
        bordered
        loading={loading}
        columns={columns}
        dataSource={displayData}
        rowKey={(r, i) => `komp-${r.nopen}-${r.item_code}-${i}`}
        rowClassName={(r) => {
          const st = getStatus(r.selisih);
          return st === "match"
            ? "row-match"
            : st === "lebih_dok"
              ? "row-lebih-dok"
              : "row-lebih-fisik";
        }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: filterStatus ? displayData.length : pagination.total,
          showSizeChanger: true,
          size: "small",
          showTotal: (t, r) => `${r[0]}-${r[1]} dari ${t}`,
          pageSizeOptions: ["20", "50", "100"],
        }}
        onChange={handleTable}
        scroll={{ x: 1350 }}
      />

      {/* ── Modal detail item per kategori ── */}
      <ItemModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        statusKey={modalStatus}
        items={modalStatus ? itemsByStatus(modalStatus) : []}
      />

      <style>{`
                .row-match td { background-color: #f6ffed !important; }
                .row-lebih-dok td { background-color: #fffbe6 !important; }
                .row-lebih-fisik td { background-color: #fff2f0 !important; }
            `}</style>
    </>
  );
}

/* ───────────────────────────────────────────────
   Main Page
─────────────────────────────────────────────── */
export default function LaporanInternal() {
  const tabItems = [
    {
      key: "dokumen",
      label: "Stok Berdasarkan Dokumen",
      children: <StokDokumen />,
    },
    { key: "fisik", label: "Stok Berdasarkan Fisik", children: <StokFisik /> },
    {
      key: "komparasi",
      label: "Laporan Dokumen vs Fisik",
      children: <StokKomparasi />,
    },
  ];

  return (
    <Card bodyStyle={{ padding: "16px" }}>
      <Row gutter={[16, 8]} style={{ marginBottom: 12 }}>
        <Col xs={24}>
          <Title level={4} style={{ margin: 0 }}>
            Laporan Internal Inventory
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Stok berdasarkan dokumen, stok fisik, dan perbandingan antar
            keduanya
          </Text>
        </Col>
      </Row>
      <Tabs defaultActiveKey="dokumen" items={tabItems} />
    </Card>
  );
}
